import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import { v4 as uuidv4 } from 'uuid'

export interface BreadcrumbData {
  id: string
  title: string
  pathname: string
  search?: string
  state?: any
}

interface BreadcrumbsState {
  crumbs: BreadcrumbData[]
}

type BreadcrumbsAction =
  | { type: 'ADD_CRUMB'; payload: BreadcrumbData }
  | { type: 'UPDATE_CRUMB'; payload: BreadcrumbData }
  | { type: 'REMOVE_CRUMB'; payload: { id: string } }

interface BreadcrumbsContextType {
  state: BreadcrumbsState
  addCrumb: (data: Omit<BreadcrumbData, 'id'>) => void
  updateCrumb: (data: BreadcrumbData) => void
  removeCrumb: (id: string) => void
}

const initialState: BreadcrumbsState = {
  crumbs: [],
}

// Helper function to normalize paths for comparison
const normalizePath = (path: string): string => {
  // Remove trailing slash and ensure it starts with a slash
  return '/' + path.replace(/^\/|\/$/g, '')
}

const breadcrumbsReducer = (state: BreadcrumbsState, action: BreadcrumbsAction): BreadcrumbsState => {
  switch (action.type) {
    case 'ADD_CRUMB':
      const normalizedNewPath = normalizePath(action.payload.pathname)
      const existingCrumbIndex = state.crumbs.findIndex((crumb) => normalizePath(crumb.pathname) === normalizedNewPath)

      if (existingCrumbIndex !== -1) {
        return {
          ...state,
          crumbs: state.crumbs.map((crumb, index) => (index === existingCrumbIndex ? action.payload : crumb)),
        }
      }

      return {
        ...state,
        crumbs: [...state.crumbs, action.payload],
      }
    case 'UPDATE_CRUMB':
      return {
        ...state,
        crumbs: state.crumbs.map((crumb) => (crumb.id === action.payload.id ? action.payload : crumb)),
      }
    case 'REMOVE_CRUMB':
      return {
        ...state,
        crumbs: state.crumbs.filter((crumb) => crumb.id !== action.payload.id),
      }
    default:
      return state
  }
}

const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(undefined)

export const BreadcrumbsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(breadcrumbsReducer, initialState)

  const addCrumb = (data: Omit<BreadcrumbData, 'id'>) => {
    dispatch({
      type: 'ADD_CRUMB',
      payload: { id: uuidv4(), ...data },
    })
  }

  const updateCrumb = (data: BreadcrumbData) => {
    dispatch({
      type: 'UPDATE_CRUMB',
      payload: data,
    })
  }

  const removeCrumb = (id: string) => {
    dispatch({
      type: 'REMOVE_CRUMB',
      payload: { id },
    })
  }

  return (
    <BreadcrumbsContext.Provider value={{ state, addCrumb, updateCrumb, removeCrumb }}>
      {children}
    </BreadcrumbsContext.Provider>
  )
}

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbsContext)
  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbsProvider')
  }
  return context
}
